import { Injectable, inject } from '@angular/core';
import { singleConnect } from '@core/utils';
import type { NgxsOnInit, StateContext } from '@ngxs/store';
import {
	Action,
	State,
	StateToken,
	createPropertySelectors,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { of, retry, timer } from 'rxjs';
import { ClientIntakeResponsesSubCollectionRepo } from '../clientIntakeResponses.firebase.repo';
import { CurrentClientIntakeResponseStateModel } from './currentClientIntakeResponse.firebase.models';
import {
	SetCurrentClientIntakeResponse,
	SetCurrentClientIntakeResponseConnection,
	ClearCurrentClientIntakeResponseConnection,
} from './currentClientIntakeResponse.firebase.actions';

export const CURRENT_CLIENT_INTAKE_RESPONSE_STATE_TOKEN =
	new StateToken<CurrentClientIntakeResponseStateModel>(
		'currentClientIntakeResponse',
	);

export const $PROPS = createPropertySelectors(
	CURRENT_CLIENT_INTAKE_RESPONSE_STATE_TOKEN,
);

export const CURRENT_CLIENT_INTAKE_FORMS_STATE_DEFAULTS: CurrentClientIntakeResponseStateModel =
	{
		query: {
			connected: false,
			pathParams: null,
		},
		clientIntakeResponse: null,
	};

@State<CurrentClientIntakeResponseStateModel>({
	name: CURRENT_CLIENT_INTAKE_RESPONSE_STATE_TOKEN,
	defaults: CURRENT_CLIENT_INTAKE_FORMS_STATE_DEFAULTS,
})
@Injectable()
export class CurrentClientIntakeResponseState implements NgxsOnInit {
	private subCollectionRepo = inject(ClientIntakeResponsesSubCollectionRepo);
	static _props = $PROPS;

	ngxsOnInit(): void {
		singleConnect(
			CurrentClientIntakeResponseState._props.query,
			(query) => {
				if (
					!query.connected ||
					!query.pathParams?.businessId ||
					!query.pathParams?.matterId ||
					!query.pathParams?.clientIntakeFormId ||
					!query.pathParams?.clientIntakeResponseId
				) {
					return of(null);
				}

				return this.subCollectionRepo
					.getCollection(query.pathParams)
					.getById(query.pathParams.clientIntakeResponseId)
					.pipe(
						retry({
							count: 10,
							delay: (error, retryCount) => {
								const backoffTime = Math.min(
									500 * Math.pow(2, retryCount - 1),
									30000,
								);
								return timer(backoffTime);
							},
						}),
					);
			},
			(data) => {
				return new SetCurrentClientIntakeResponse(data);
			},
		);
	}

	@Action(SetCurrentClientIntakeResponseConnection)
	setCurrentClientIntakeFormsConnection(
		ctx: StateContext<CurrentClientIntakeResponseStateModel>,
		action: SetCurrentClientIntakeResponseConnection,
	): void {
		ctx.setState(
			patch({
				query: patch({
					connected: action.connect,
					pathParams: action.pathParams,
				}),
			}),
		);
	}

	@Action(ClearCurrentClientIntakeResponseConnection)
	clearCurrentClientIntakeFormsConnection(
		ctx: StateContext<CurrentClientIntakeResponseStateModel>,
	): void {
		ctx.setState(
			patch({
				query: patch({
					connected: false,
					pathParams: null,
				}),
			}),
		);
	}

	@Action(SetCurrentClientIntakeResponse)
	setCurrentClientIntakeResponse(
		ctx: StateContext<CurrentClientIntakeResponseStateModel>,
		{ currentClientIntakeResponse }: SetCurrentClientIntakeResponse,
	): void {
		ctx.patchState({
			clientIntakeResponse: currentClientIntakeResponse,
		});
	}
}
